import { useSelector } from 'react-redux';
import { useLocation } from 'react-use';

const VITE_PRERELEASE_FEATURE_TESTING = import.meta.env
  .VITE_PRERELEASE_FEATURE_TESTING;

/**
 * Return true if pre-release features are enabled for the current user/session
 *
 * ```ts
 * const preReleaseFeature = usePreReleaseFeatures();
 *
 * return <SomeComponent showNewButton={preReleaseFeature} />
 * ```
 *
 * @returns boolean
 */
export const usePreReleaseFeatures = () => {
  const { search } = useLocation();
  const params = new URLSearchParams(search || '');
  const hasSettingsPermission = !!useSelector(
    (store) =>
      store?.authentication?.access?.sections?.settings_section
        ?.pre_release_features
  );
  const override =
    String(VITE_PRERELEASE_FEATURE_TESTING) === 'true' &&
    params.get('prerelease_feature_override') === 'true';
  return hasSettingsPermission || override;
};

/**
 * Wrap a sub component in this component to determine if we should show
 * the child component when the prerelease features are enabled or not.
 *
 * ```ts
 * return (
 *   <PreReleaseFeature>
 *      <MyBetaFeatureComponent />
 *   </PreReleaseFeature>
 * )
 * ```
 *
 * @returns
 */
export const PreReleaseFeature = ({ children }) => {
  const preReleaseFeature = usePreReleaseFeatures();
  if (preReleaseFeature) {
    return <>{children}</>;
  }
  return null;
};
